import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Archive,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Users,
  NotebookPen,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import type { EventItem, EventPastItem } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventStatusBadge } from '../EventStatusBadge';
import { EventOutcomeBadge } from '../EventOutcomeBadge';
import { EventItemModal } from '../EventItemModal';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

const PAGE_SIZE = 25;

export interface EventArchivePanelProps {
  perms: EventTeamPerms;
}

/**
 * Past Events Archive (guide §1.10): completed/cancelled events with their
 * retrospectives inline — "an easy way to see past events to see what worked
 * and didn't". "Run it again" clones the event and links it `follow_up_of`.
 */
export const EventArchivePanel = ({ perms }: EventArchivePanelProps) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventPastItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rerunSource, setRerunSource] = useState<EventPastItem | null>(null);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await eventTeamApiService.listPastEvents(targetPage, PAGE_SIZE);
      setEvents(res.results);
      setTotal(res.total);
      setPage(targetPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the archive');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(1);
  }, [load]);

  /** After the clone is created, link it back `follow_up_of` (guide §1.10). */
  const handleRerunCreated = async (created: EventItem) => {
    if (rerunSource) {
      try {
        await eventTeamApiService.createEntityLink('event', created.id, {
          target_id: rerunSource.id,
          target_type: 'event',
          link_type: 'follow_up_of',
        });
      } catch (err) {
        console.warn('Failed to create follow_up_of link:', err);
        alert(
          'Event created, but the follow-up link failed. Add it from the Related tab.',
        );
      }
    }
    setRerunSource(null);
    navigate(`/event-team/events/${created.id}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Past Events"
        icon={Archive}
        iconClassName="text-glass-400"
        count={total}
        description="Completed and cancelled events with their retrospectives inline."
        className="mb-0"
      />

      {loading ? (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-2">
              <SkeletonLoader className="h-4 w-56 max-w-full" />
              <div className="flex gap-2">
                <SkeletonLoader className="h-5 w-20 rounded-full" />
                <SkeletonLoader className="h-5 w-24 rounded-full" />
              </div>
              <SkeletonLoader className="h-16 w-full" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      ) : events.length === 0 ? (
        <EmptyState icon={Archive} title="No past events" description="Completed and cancelled events will appear here." />
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const retro = event.retrospective;
            return (
              <div key={event.id} className="glass-card p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/event-team/events/${event.id}`)}
                      className="text-sm font-semibold text-white hover:text-accent-300 transition-colors inline-flex items-center gap-1.5 text-left"
                    >
                      {event.title}
                      <ExternalLink className="w-3.5 h-3.5 text-glass-500" />
                    </button>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-glass-500 tabular-nums">
                      <EventStatusBadge status={event.status} type="event" />
                      {event.category && (
                        <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{event.category}</span>
                      )}
                      {event.scheduled_start && <span>Ran {format(new Date(event.scheduled_start), 'MMM d, yyyy')}</span>}
                      {event.completed_at && <span>Closed {format(new Date(event.completed_at), 'MMM d, yyyy')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {perms.canLead && !retro && (
                      <button
                        type="button"
                        onClick={() => navigate(`/event-team/events/${event.id}`)}
                        className="glass-button-secondary btn-xs text-emerald-300"
                        title="Write the retrospective on the event page"
                      >
                        <NotebookPen className="w-3.5 h-3.5" />
                        Write retro
                      </button>
                    )}
                    {perms.canCreate && (
                      <button
                        type="button"
                        onClick={() => setRerunSource(event)}
                        className="glass-button-sm"
                        title="Create a new event from this one (linked as a follow-up)"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Run it again
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline retrospective */}
                {retro ? (
                  <div className="mt-3 pt-3 border-t border-glass-200/10">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <EventOutcomeBadge outcome={retro.outcome} />
                      {retro.attendance_count != null && (
                        <span className="text-xs text-glass-300 inline-flex items-center gap-1 tabular-nums">
                          <Users className="w-3.5 h-3.5 text-purple-400" />
                          {retro.attendance_count} attendee{retro.attendance_count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div className="rounded-lg bg-green-500/5 border border-green-500/10 p-2">
                        <p className="eyebrow text-green-300 mb-0.5">
                          Worked
                        </p>
                        <p className="text-glass-300 line-clamp-3 whitespace-pre-wrap">{retro.what_worked || '—'}</p>
                      </div>
                      <div className="rounded-lg bg-red-500/5 border border-red-500/10 p-2">
                        <p className="eyebrow text-red-300 mb-0.5">
                          Didn't
                        </p>
                        <p className="text-glass-300 line-clamp-3 whitespace-pre-wrap">{retro.what_didnt || '—'}</p>
                      </div>
                      <div className="rounded-lg bg-cyan-500/5 border border-cyan-500/10 p-2">
                        <p className="eyebrow text-cyan-300 mb-0.5">
                          Lessons
                        </p>
                        <p className="text-glass-300 line-clamp-3 whitespace-pre-wrap">{retro.lessons || '—'}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 pt-3 border-t border-glass-200/10 text-xs text-glass-500 italic">
                    No retrospective.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-icon"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-icon"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* "Run it again" — prefilled create modal; on success we link follow_up_of. */}
      {rerunSource && (
        <EventItemModal
          isOpen={!!rerunSource}
          mode="create"
          prefill={{
            title: `${rerunSource.title} (again)`,
            description: rerunSource.description,
            category: rerunSource.category,
            event_type: rerunSource.event_type,
            priority: rerunSource.priority,
            project_id: rerunSource.project_id,
          }}
          onClose={() => setRerunSource(null)}
          onSuccess={(created) => void handleRerunCreated(created)}
        />
      )}
    </div>
  );
};
